<template>
  <filter-panel
    class="filters-panel"
    :filter-parameters="formattedFilters"
    @update="applyFilter"
    :notification-text="null"
    :api-request-data="null"/>
</template>

<script>
import cloneDeep from 'lodash-es/cloneDeep';
import getNested from 'lodash-es/get';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import FilterPanel from '../widgets/FilterPanel';
import {
  getAPIFilterValues,
  applyFilterValues,
  getFormattedFilters
} from '../widgets/FilterPanel';

import {
  getToday,
  getYearToDateRange,
  getCurrentQuarterRange,
  getCurrentYear,
} from '@/shared/date-utils';

export default {
  name: 'monitor-filters',

  components: {
    FilterPanel,
  },

  data() {
    const defaultChoices = {
      fleet: null,
      commodity: {
        value: []
      },
    };

    return {
      filtersReady: false,
      defaultChoices: defaultChoices,
      filterSchema: {
        fleet: {
          name: 'fleet',
          apiName: 'fleetId',
          type: 'multiselect',
          placeholder: 'Fleet',
          value: null,
          choices: [],
          defaultChoice: defaultChoices.fleet,
          options: {
            multiple: false,
            closeOnSelect: true,
          },
        },

        commodity: {
          name: 'commodity',
          apiName: 'commodity',
          type: 'treeselect',
          placeholder: 'Commodity',
          value: [],
          choices: [],
          rawChoices: [],
          defaultChoice: [],
          options: {
            toggleAll: true,
          },
        },

        dateRange: {
          name: ['startDate', 'endDate'],
          type: 'daterange-picker',
          options: {
            ranges: this.getDatePickerRanges(),
          },

          startDate: {
            name: 'startDate',
            apiName: 'fromDate',
            value: moment('2020-01-01').format('YYYY-MM-DD'),
          },
          endDate: {
            name: 'endDate',
            apiName: 'toDate',
            value: moment().format('YYYY-MM-DD')
          },
        },
      },
    };
  },

  methods: {
    ...mapActions('api/tradeflows', ['getCommodities']),
    ...mapActions('api/fleets', ['getFleets']),

    async loadFilterChoices() {
      await this.loadFleets();
      await this.loadCommodities();
      this.filtersReady = true;
    },

    async loadFleets() {
      let fleets = await this.getFleets();
      this.$emit('fleets-loaded', cloneDeep(fleets));

      fleets = fleets.map(
        fleet => ({value: fleet.fleetId, text: fleet.fleetName})
      );
      this.filterSchema.fleet.choices = fleets;
    },

    async loadCommodities() {
      this.filterSchema.commodity.choices = await this.getCommodities({group: true});
    },

    setDefaultValues() {
      const firstFleet = this.filterSchema.fleet.choices.find(fleet => getNested(fleet, 'value'));
      if(firstFleet) {
        this.filterSchema.fleet.value = firstFleet;
      }
    },

    getDatePickerRanges() {
      let ranges = {
        'Full period (2015-today)': [
          moment('2015-01-01').toDate(),
          getToday().toDate(),
        ],
        'QTD': getCurrentQuarterRange(),
        'YTD': getYearToDateRange(),
      };

      const currentYear = getCurrentYear();
      for(let year = currentYear - 3; year < currentYear; year++) {
        ranges[`${year}`] = [
          moment(`${year}-01-01`).toDate(),
          moment(`${year}-12-31`).toDate(),
        ];
      }

      return ranges;
    },

    applyFilter(filterValues) {
      if(!this.filtersReady) {
        return;
      }

      applyFilterValues(this.filterSchema, filterValues);
      this.$emit('search', getAPIFilterValues(this.filterSchema, filterValues));
    },

    choicesToValues(choices) {
      return choices.map(item => item.value);
    },
  },


  computed: {
    ...mapGetters(
      'api/presets', ['getActivePreset']
    ),

    formattedFilters() {
      return getFormattedFilters(this.filterSchema);
    },
  },

  watch: {
    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        if(preset && this.$route.path !== preset.viewName) {
          return;
        }

        if(!this.filtersReady) {
          await this.loadFilterChoices();
          if(!preset) {
            this.setDefaultValues();
          }
        }

        if(preset) {
          // filter panel will be updated by props values and .applyFilter() will be emitted
          applyFilterValues(this.filterSchema, getNested(preset, 'params.filterPanel', {}));
        }
      },
    },
  },
}
</script>

<style scoped>
.filters-panel >>> .multiselect__tags {
  min-width: 250px;
}

.filters-panel >>> .filter-field__select {
  min-width: 100px;
}
</style>
